import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { mapValues } from 'lodash'

import { buildRssDisplay } from '@/stay-displayer/rssRumBuilder'
import type { McoStay, RawMcoStay } from '@/store/modules/stay/mco/types'
import type { RawSmrStay, SmrStay } from '@/store/modules/stay/smr/types'
import type { RawStay } from '@/store/modules/stay/types'

// This is a recursive function that returns the same object,
// but where diagnoses (object that have a key named "diagnosis") are changed
// in order to include the level of the current stay.
const parseDiagnosisLabels = (stayEnd, obj) => {
  if (Array.isArray(obj)) {
    return obj.map((item) => parseDiagnosisLabels(stayEnd, item))
  }
  if (typeof obj === 'object' && obj !== null) {
    return mapValues(obj, (value, key) => {
      if (!value || !key) {
        return value
      }
      if (key.toLowerCase().includes('diag') && Array.isArray(value) && value.every((itm) => Array.isArray(itm.diagnosisVersions))) {
        return value.map((subvalue) => {
          const correctVersion = subvalue.diagnosisVersions.find((ver) => dayjs(ver.startDate).isSameOrBefore(stayEnd) && dayjs(ver.endDate).isAfter(stayEnd))
          return {
            ...subvalue,
            level: correctVersion ? correctVersion.level : 1
          }
        })
      }
      if (key.toLowerCase().includes('diag') && Array.isArray(value.diagnosisVersions)) {
        const correctVersion = value.diagnosisVersions.find((ver) => dayjs(ver.startDate).isSameOrBefore(stayEnd) && dayjs(ver.endDate).isAfter(stayEnd))
        return {
          ...value,
          level: correctVersion ? correctVersion.level : 1
        }
      }
      return parseDiagnosisLabels(stayEnd, value)
    })
  }
  return obj
}

function isRawMcoStay(stay: RawStay): stay is RawMcoStay {
  return 'rums' in stay
}

// reports/categoricalLabResults/labResults/drugEvents/textualHealthEntries/healthConstants all carry
// their own creationDate as a raw ISO string — this converts it to a Dayjs, passing every other field
// through untouched.
function withParsedCreationDate<T extends { creationDate?: string | null }>(items: T[]) {
  return items.map((item) => ({
    ...item,
    creationDate: item.creationDate ? dayjs(item.creationDate) : null,
  }))
}

export function simpleParseStay(stay: RawMcoStay): McoStay
export function simpleParseStay(stay: RawSmrStay): SmrStay
// Generic overload for callers that only know the shared RawStay shape, not specifically which of
// MCO/SMR they're handling.
export function simpleParseStay(stay: RawStay): McoStay | SmrStay
export function simpleParseStay(stay: RawStay): McoStay | SmrStay {
  const stayEnd = dayjs(stay.stayEnd)
  const parsedStay = {
    ...stay,
    patient: {
      ...stay.patient,
      birthDate: stay.patient.birthDate ? dayjs(stay.patient.birthDate) : null,
    },
    lastPredictionUpdate: stay.lastPredictionUpdate ? dayjs(stay.lastPredictionUpdate) : null,
    lastHealthDataUpdate: stay.lastHealthDataUpdate ? dayjs(stay.lastHealthDataUpdate) : null,
    lastExternalPmsiUpdate: stay.lastExternalPmsiUpdate ? dayjs(stay.lastExternalPmsiUpdate) : null,
    stayStart: dayjs(stay.stayStart),
    stayEnd,
    reports: withParsedCreationDate(stay.reports),
    // categoricalLabResults/labResults/drugEvents/healthConstants aren't sent at all by
    // stay_full/stay_smr_full (see Domain.Stay.Stay.yml/Entity.FrSmr.Stay.yml) — only /documents
    // populates them, via mergeStayDocuments. Absent (not just empty) until then.
    categoricalLabResults: withParsedCreationDate(stay.categoricalLabResults ?? []),
    labResults: withParsedCreationDate(stay.labResults ?? []),
    drugEvents: withParsedCreationDate(stay.drugEvents ?? []),
    textualHealthEntries: withParsedCreationDate(stay.textualHealthEntries),
    healthConstants: withParsedCreationDate(stay.healthConstants ?? []),
  }

  if (isRawMcoStay(stay)) {
    const parsedRums = stay.rums.map((rum) => ({
      ...parseDiagnosisLabels(stayEnd, rum),
      rumStart: dayjs(rum.rumStart),
      rumEnd: dayjs(rum.rumEnd),
    }))

    const rssRum = parsedRums.length > 1 ? buildRssDisplay(stay.rums) : null

    return {
      ...parsedStay,
      rums: parsedRums,
      rssRum: rssRum
    } as McoStay
  }

  return parsedStay as SmrStay
}
